import type { DashboardWidgetDto, DashboardWidgetType } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

export const GRID_COLUMNS = 6;
export const MAX_ROW_SPAN = 8;

export type LayoutRect = Pick<DashboardWidgetDto, "position_x" | "position_y" | "width" | "height">;

export type LayoutPatch = Partial<LayoutRect>;

export function clampLayoutRect(layout: LayoutRect): LayoutRect {
  const width = Math.min(Math.max(layout.width, 1), GRID_COLUMNS);
  const height = Math.min(Math.max(layout.height, 1), MAX_ROW_SPAN);
  const position_x = Math.max(0, Math.min(layout.position_x, GRID_COLUMNS - width));
  const position_y = Math.max(0, layout.position_y);
  return { position_x, position_y, width, height };
}

export function mergeLayout(base: DashboardWidgetDto, patch?: LayoutPatch | null): LayoutRect {
  return clampLayoutRect({
    position_x: patch?.position_x ?? base.position_x,
    position_y: patch?.position_y ?? base.position_y,
    width: patch?.width ?? base.width,
    height: patch?.height ?? base.height,
  });
}

export function mergeWidgetConfigJson(
  base: JsonObject | null | undefined,
  patch: JsonObject | null | undefined,
): JsonObject {
  return { ...(base ?? {}), ...(patch ?? {}) };
}

/** Merged DTO for runtime (WidgetRenderer) + grid placement. */
export function mergeWidgetForDisplay(
  base: DashboardWidgetDto,
  layoutPatch?: LayoutPatch | null,
  configPatch?: JsonObject | null,
): DashboardWidgetDto {
  const layout = mergeLayout(base, layoutPatch);
  return {
    ...base,
    ...layout,
    config: mergeWidgetConfigJson(base.config, configPatch),
  };
}

function cellsOccupied(rect: LayoutRect): Set<string> {
  const s = new Set<string>();
  for (let y = rect.position_y; y < rect.position_y + rect.height; y += 1) {
    for (let x = rect.position_x; x < rect.position_x + rect.width; x += 1) {
      s.add(`${x},${y}`);
    }
  }
  return s;
}

function canPlace(rect: LayoutRect, ignoreKey: string | null, layouts: { id: string; rect: LayoutRect }[]): boolean {
  const c = clampLayoutRect(rect);
  if (c.position_x + c.width > GRID_COLUMNS) {
    return false;
  }
  const mine = cellsOccupied(c);
  for (const cell of mine) {
    const [xs] = cell.split(",").map(Number) as [number, number];
    if (xs < 0 || xs >= GRID_COLUMNS) {
      return false;
    }
    for (const { id, rect } of layouts) {
      if (ignoreKey != null && id === ignoreKey) {
        continue;
      }
      if (cellsOccupied(rect).has(cell)) {
        return false;
      }
    }
  }
  return true;
}

/** Next top-left for a new widget (defaultW × defaultH), avoiding overlap with current layouts. */
/** True if `rect` does not overlap any other widget's effective layout (server + dirty). */
export function isLayoutFreeForWidget(
  rect: LayoutRect,
  ignoreWidgetId: string,
  widgets: DashboardWidgetDto[],
  dirtyLayout: Record<string, LayoutPatch>,
): boolean {
  const layouts = widgets
    .filter((w) => w.id !== ignoreWidgetId)
    .map((w) => ({ id: w.id, rect: mergeLayout(w, dirtyLayout[w.id] ?? null) }));
  return canPlace(rect, null, layouts);
}

export function defaultWidgetSize(type: DashboardWidgetType): { width: number; height: number } {
  switch (type) {
    case "line_chart":
      return { width: 3, height: 2 };
    case "gauge":
      return { width: 2, height: 2 };
    case "stat":
      return { width: 2, height: 1 };
    case "table":
      return { width: 3, height: 3 };
    default:
      return { width: 2, height: 2 };
  }
}

export function findNextWidgetSlot(
  widgets: DashboardWidgetDto[],
  defaultW: number,
  defaultH: number,
): LayoutRect {
  const w = Math.min(Math.max(defaultW, 1), GRID_COLUMNS);
  const h = Math.min(Math.max(defaultH, 1), MAX_ROW_SPAN);
  const layouts = widgets.map((x) => ({ id: x.id, rect: mergeLayout(x, null) }));

  for (let y = 0; y < 64; y += 1) {
    for (let x = 0; x <= GRID_COLUMNS - w; x += 1) {
      const candidate = clampLayoutRect({ position_x: x, position_y: y, width: w, height: h });
      if (canPlace(candidate, null, layouts)) {
        return candidate;
      }
    }
  }
  return clampLayoutRect({ position_x: 0, position_y: 0, width: w, height: h });
}
